import { Component, OnDestroy, OnInit } from '@angular/core';
import { Conversation } from '../../../reservation/reservation.model';
import { SelectionModel } from '@angular/cdk/collections';
import { ThemePalette } from '@angular/material/core';
import { MessageService } from '../../message.service';
import { TasksDialogComponent } from '../../../tasks/tasks-dialog/tasks-dialog.component';
import { TableName } from '../../../common/enums/TableName';
import { MatDialog } from '@angular/material/dialog';
import { DataBagService } from '../../../common/data-bag.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageFiltersComponent } from '../../message-filters/message-filters.component';
import { Subscription } from 'rxjs';
import { InfoboxService } from '../../../common/infobox/infobox.service';
import { DialogType, MessageAssignDialogComponent } from '../message-assign-dialog/message-assign-dialog.component';

@Component({
	selector: 'app-message-table',
	templateUrl: './message-table.component.html',
	styleUrls: ['./message-table.component.scss'],
	providers: [MessageFiltersComponent],
})
export class MessageTableComponent implements OnInit, OnDestroy {
	displayedColumns: string[] = ['checkbox', 'watch', 'subject', 'date', 'archive'];
	selection = new SelectionModel<Conversation>(true, []);
	protected checkboxColor: ThemePalette = 'primary';
	protected loadingStates: Map<string, boolean> = new Map();
	private filtersSubscription: Subscription;
	// @Output() itemReady = new EventEmitter();

	constructor(
		public service: MessageService,
		private readonly dialog: MatDialog,
		private readonly bag: DataBagService,
		private readonly _snackBar: MatSnackBar,
		private readonly infobox: InfoboxService,
	) {}

	ngOnInit() {
		this.filtersSubscription = this.service.setSelection.subscribe(() => {
			if (this.service.previousSelection.length) {
				const filteredArray = this.cachedThreads.filter((item) =>
					this.service.previousSelection.some((obj) => obj.id === item.id),
				);
				this.selection.clear();
				filteredArray.forEach((element) => {
					this.selection.select(element);
				});
				this.service.previousSelection = filteredArray;
			}
		});
	}

	protected isAllSelected() {
		return this.selection.selected.length === this.cachedThreads.length;
	}

	protected toggleAll() {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.cachedThreads.forEach((row) => this.selection.select(row));
		}
		this.service.previousSelection = this.selection.selected;
	}

	protected toggleCheckbox(element: Conversation) {
		this.selection.toggle(element);
		this.service.previousSelection = this.selection.selected;
	}

	protected get cachedThreads(): Conversation[] {
		const queryString = this.service.activeCacheKey.replace(/&limit=\d+&offset=\d+/g, '');
		const threads = Object.keys(this.service.cache)
			.filter((key) => key.includes(queryString))
			.map((key) => this.service.cache[key]);
		return threads.map((group) => group.results).reduce((acc, results) => acc.concat(results), []);
	}

	protected async changeWatchingState(thread: Conversation) {
		this.loadingStates.set(thread.id + '_watch', true);
		await (thread.watching ? this.service.unwatchThread(thread.id) : this.service.watchThread(thread.id));
		this.service.previousSelection = this.selection.selected;
		this.service.setFilters.next(true);
		setTimeout(() => {
			this.loadingStates.set(thread.id + '_watch', false);
		}, 200);
	}

	protected async openTaskDialog(content: string) {
		this.dialog
			.open(TasksDialogComponent, {
				disableClose: true,
				width: '1000px',
				data: {
					editable: true,
					tableName: TableName.USER_PROFILE,
					tableId: this.service.loggedUserId,
					content: content,
				},
			})
			.afterClosed()
			.subscribe((task) => {
				if (!task) return;
				this.bag.post(`/task_note/task/`, task, (result, error) => {
					if (result) {
						this.openSnackBar('Pomyślnie utworzono zadanie!', 'OK');
					} else throw new Error(JSON.stringify(error?.error));
				});
			});
	}

	private openSnackBar(message: string, action: string) {
		this._snackBar.open(message, action, { duration: 5000 });
	}

	protected async changeArchivedAndSetFilers(thread: Conversation) {
		this.service.archiving = true;
		await this.changeArchivedState(thread);
		this.service.previousSelection = this.selection.selected;
		this.service.setFilters.next(true);
	}

	private async changeArchivedState(thread) {
		this.loadingStates.set(thread.id + '_archived', true);
		if (this.isArchived(thread)) {
			thread.labels = thread.labels.filter((v: string) => v !== 'archived');
		} else thread.labels.push('archived');

		await this.service.patchThread(thread.id, { labels: thread.labels });
		setTimeout(() => {
			this.loadingStates.set(thread.id + '_archived', false);
		}, 200);
	}

	protected isArchived(thread: Conversation) {
		return thread.labels.includes('archived');
	}

	isWatchedSelectedThreads() {
		return this.selection.selected.every((thread) => thread.watching);
	}

	protected isArchivedSelectedThreads() {
		return this.selection.selected.every((thread) => this.isArchived(thread));
	}

	archiveThreads() {
		const requests = [];
		this.service.archiveAllLoading = true;
		this.service.archiving = true;
		this.selection.selected.forEach(async (thread) => {
			requests.push(this.changeArchivedState(thread));
		});
		Promise.all(requests).then(() => {
			this.openSnackBar(
				this.isArchivedSelectedThreads() ? 'Pomyślnie zaarchiwizowano' : 'Pomyślnie przywrócono',
				'OK',
			);
			this.selection.clear();
			this.service.archiveAllLoading = false;
			this.service.setFilters.next(true);
		});
	}

	async watchThreads() {
		this.service.watchAllLoading = true;
		const isWatched = this.isWatchedSelectedThreads();
		const watchThreadExpression = isWatched
			? (thread: Conversation) => this.service.unwatchThread(thread.id)
			: (thread: Conversation) => this.service.watchThread(thread.id);

		const snackBarMessage: string = isWatched ? 'Usunięto z obserwowanych' : 'Dodano do obserwowanych';

		const requests = this.selection.selected.map((thread) => watchThreadExpression(thread));

		await Promise.all(requests);
		this.service.watchAllLoading = false;
		this.service.setFilters.next(true);
		this.openSnackBar(snackBarMessage, 'OK');
	}

	protected async assignUsers(threadId: string) {
		const tableObj = {
			tableName: TableName.USER_PROFILE,
			tableId: this.service.loggedUserId,
		};
		this.service.patchThread(threadId, tableObj).then(() => this.openSnackBar('Przypisano', 'OK'));
	}

	async assignMeToSelected() {
		const requests = [];
		this.selection.selected.forEach((thread) => {
			requests.push(this.assignUsers(thread.id));
		});
		await Promise.all(requests);
		this.service.setFilters.next(true);
	}

	protected openAssignMessageDialog(threads: Conversation[], type: DialogType) {
		this.dialog
			.open(MessageAssignDialogComponent, {
				width: '500px',
				data: { thread: threads, type: type },
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.openSnackBar('Przypisano', 'OK');
					this.service.setFilters.next(true);
				}
			});
	}

	protected openAssignReservationDialogForSelectedThreads() {
		this.openAssignMessageDialog(this.selection.selected, DialogType.RESERVATION);
	}

	protected openAssignUserDialogForSelectedThreads() {
		this.openAssignMessageDialog(this.selection.selected, DialogType.USER);
	}

	ngOnDestroy() {
		this.filtersSubscription?.unsubscribe();
	}

	protected readonly DialogType = DialogType;
}
